import { useUnitDisplay } from "@/composables/useUnitDisplay";
import type { DistanceUnit } from "@/models/siteUnit.types";
import { type ComputedRef, computed } from "vue";
import type { SpreadingCompactionUnitConfig } from "./unitConfig.types";
import {
	convertDisplayToMm,
	convertMmToDisplay,
	displayStep,
	formatDisplayInput,
	normalizeDistanceUnit,
	quantizeDisplayValue,
	resolveSpreadingCompactionUnitConfig,
} from "./unitConverters";

export interface SpreadingCompactionUnitContext {
	unitConfig: ComputedRef<SpreadingCompactionUnitConfig>;
	convertDisplayToMm: (displayValue: number) => number;
	convertMmToDisplay: (internalMm: number) => number;
	/** Snaps an input value to the display granularity. */
	quantizeDisplayValue: (displayValue: number) => number;
	/** Smallest settable value in the display unit. */
	displayStep: ComputedRef<number>;
	/** Internal mm -> input field string at the display granularity. */
	formatDisplayInput: (internalMm: number) => string;
	formatOffsetLabel: (internalMm: number, decimals?: number) => string;
}

function round(value: number, decimals: number): number {
	const base = 10 ** decimals;
	return Math.round(value * base) / base;
}

/**
 * Adds the mm <-> display-unit handling this feature needs on top of the shared
 * unit boundary. Site units, labels and m <-> site-unit conversion stay in `useUnitDisplay`.
 */
export function useSpreadingCompactionUnit(): SpreadingCompactionUnitContext {
	const { siteUnits } = useUnitDisplay();

	const distanceUnit = computed<DistanceUnit>(() =>
		normalizeDistanceUnit(siteUnits.value.distance),
	);

	const unitConfig = computed(() =>
		resolveSpreadingCompactionUnitConfig(distanceUnit.value),
	);
	function convertDisplayToMmWithContext(displayValue: number): number {
		return convertDisplayToMm(displayValue, unitConfig.value);
	}

	function convertMmToDisplayWithContext(internalMm: number): number {
		return convertMmToDisplay(internalMm, unitConfig.value);
	}

	function quantizeDisplayValueWithContext(displayValue: number): number {
		return quantizeDisplayValue(displayValue, unitConfig.value);
	}

	function formatDisplayInputWithContext(internalMm: number): string {
		return formatDisplayInput(internalMm, unitConfig.value);
	}

	const step = computed(() => displayStep(unitConfig.value));

	function formatOffsetLabel(internalMm: number, decimals?: number): string {
		const actualDecimals = decimals ?? unitConfig.value.displayDecimals;
		const value = round(
			convertMmToDisplayWithContext(internalMm),
			actualDecimals,
		);
		return `${value}${unitConfig.value.displayUnit}`;
	}

	return {
		unitConfig,
		convertDisplayToMm: convertDisplayToMmWithContext,
		convertMmToDisplay: convertMmToDisplayWithContext,
		quantizeDisplayValue: quantizeDisplayValueWithContext,
		displayStep: step,
		formatDisplayInput: formatDisplayInputWithContext,
		formatOffsetLabel,
	};
}
